import { Injectable, inject, OnDestroy } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  isChunkLoadError,
  isChunkScriptUrl,
} from '../../utils/chunk-load-error.util';

interface VersionManifest {
  buildId: string;
  builtAt?: string;
}

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private readonly router = inject(Router);

  private readonly updateAvailableSubject = new BehaviorSubject(false);
  readonly updateAvailable$ = this.updateAvailableSubject.asObservable();

  private baselineBuildId: string | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private initialized = false;
  private routerSub?: { unsubscribe(): void };

  private readonly onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (isChunkLoadError(event.reason)) {
      this.markUpdateAvailable();
    }
  };

  private readonly onWindowError = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof HTMLScriptElement)) {
      return;
    }
    const src = target.src || '';
    if (!src || !isChunkScriptUrl(src)) {
      return;
    }
    this.markUpdateAvailable();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void this.checkForUpdate();
    }
  };

  initialize(): void {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }
    if (!environment.appUpdateCheckEnabled) {
      return;
    }

    this.initialized = true;
    void this.checkForUpdate();

    this.pollTimer = setInterval(
      () => void this.checkForUpdate(),
      environment.appUpdatePollIntervalMs,
    );

    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
    window.addEventListener('error', this.onWindowError, true);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        if (isChunkLoadError(event.error)) {
          this.markUpdateAvailable();
        }
      });
  }

  markUpdateAvailable(): void {
    if (!this.updateAvailableSubject.value) {
      this.updateAvailableSubject.next(true);
    }
  }

  reload(): void {
    window.location.reload();
  }

  ngOnDestroy(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
    window.removeEventListener('error', this.onWindowError, true);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.routerSub?.unsubscribe();
    this.routerSub = undefined;
  }

  private async checkForUpdate(): Promise<void> {
    try {
      const manifest = await this.fetchVersionManifest();
      if (!manifest?.buildId) {
        return;
      }

      if (this.baselineBuildId === null) {
        this.baselineBuildId = manifest.buildId;
        return;
      }

      if (manifest.buildId !== this.baselineBuildId) {
        this.markUpdateAvailable();
      }
    } catch {
      // Ignore transient network failures during polling.
    }
  }

  private async fetchVersionManifest(): Promise<VersionManifest | null> {
    const response = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as VersionManifest;
  }
}
